import { useMemo, useRef, useState } from "react";
import { ChatNavigationLayout } from "@photon/chat-react/chat-layout";
import { LogOut } from "lucide-react";
import useClickOutside from "../../../hooks/useClickOutside";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ChatStore from "../../../store/chatStore";
import { updateChatTitle } from "../../../utils/sparkApi/updateChatTitle";
import FileUploadStore, { FileStore } from "../../../store/fileUploadStore";
import PromptStore from "../../../store/promptStore";
import UIStore from "../../../store/uiStore";
import { useGetSparks, useMoveChatAPI } from "../../../api/queryHooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  BlackDot,
  DeleteIcon,
  Dot,
  DragDisabled,
  DragIcon,
  EditIcon,
  PinIconFilled,
  PinIconLight,
  UnpinIcon,
} from "../../../assets";
import useGenerateApiSignal from "../../../store/useGenerateApiSignal";
import { createPortal } from "react-dom";
import UserAuthStore from "../../../store/userStore";
import { getNavigateLink, isChatActive } from "../../../utils/helpers";
import { PERMISSIONS } from "../../../utils/permissions";
import useSessionIdStore from "../../../store/useSessionIdStore";
import { pinChatAPI } from "../../../utils/sparkApi/pinChat";
import { useSparkDeletion } from "../../../hooks/chat/useSparkDeletion";
import QUERYKEYS from "../../../api/queryKeys";
import { useTheme } from "../../../context/ThemeProvider";

export const ChatMenu = ({
  menuButtonRef,
  activeDropdown,
  uid,
  handlePinChat,
  toggleEdit,
  title,
  chat,
  handleDotClick,
  theme,
  pinned,
  dropdownRef,
  space,
  dropdownPosition,
}) => {
  const { deleteSpark } = useSparkDeletion(space);
  const { mutate: moveChatMutate } = useMoveChatAPI();
  const moveUser = UserAuthStore((state) => state.user);
  const moveOrgId = UserAuthStore((state) => state.orgId);

  const handleMoveToPersonal = () => {
    handleDotClick(uid);
    moveChatMutate({
      old_sid: space.space_id,
      chat_ids: [uid],
      org_id: moveOrgId,
      uid: moveUser?.uid,
      session_id: useSessionIdStore.getState().session_id,
    });
  };

  const getDropdownStyle = () => {
    if (!menuButtonRef.current) return {};

    const rect = menuButtonRef.current.getBoundingClientRect();

    if (dropdownPosition === "top") {
      return {
        left: rect.left + 30,
        bottom: window.innerHeight - rect.top - 26,
      };
    } else {
      return {
        left: rect.left + 30,
        top: rect.top - 12,
      };
    }
  };

  return (
    <div
      className="menu-container flex items-center absolute right-[5px]"
      data-active={activeDropdown === uid ? "true" : "false"}
    >
      <ChatTitleDotMenu
        menuButtonRef={menuButtonRef}
        chat={chat}
        handleDotClick={handleDotClick}
        theme={theme}
        isOpen={activeDropdown === uid}
      />
      {activeDropdown === uid &&
        createPortal(
          <div
            ref={dropdownRef}
            className={`fixed bg-fill-white border border-edge-gray-200 dark:bg-fill-panel-surface dark:border-edge-panel-border dark:text-ink-white rounded-xl shadow-lg backdrop-blur-sm p-1 min-w-[120px] z-[100]`}
            style={getDropdownStyle()}
          >
            <button
              onClick={() => {
                handleDotClick(uid); // Close the dropdown
                handlePinChat(uid);
              }}
              className="chat-menu-option w-full flex items-center px-3 py-2 text-[12px] text-ink-gray-700 dark:text-ink-gray-200 hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700 rounded-lg transition-all duration-200"
            >
              {pinned ? (
                <>
                  <UnpinIcon className="chat-menu-option w-3.5 h-3.5 mr-2.5 text-ink-ink dark:text-ink-white fill-ink-ink dark:fill-ink-white " />
                  Unpin
                </>
              ) : (
                <>
                  <PinIconLight className="chat-menu-option w-3.5 h-3.5 mr-2.5 fill-ink-gray-700 dark:fill-ink-gray-200" />
                  Pin
                </>
              )}
            </button>
            {PERMISSIONS.EDIT_SPARK_IN_SPECTRA(space) ? (
              <button
                onClick={() => {
                  handleDotClick(uid); // Close the dropdown
                  toggleEdit(uid, title);
                }}
                className="chat-menu-option w-full flex items-center px-3 py-2 text-[12px] text-ink-gray-700 dark:text-ink-gray-200 hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700 rounded-lg transition-all duration-200"
              >
                <EditIcon className="chat-menu-option w-3.5 h-3.5 mr-2.5 dark:fill-ink-white fill-ink-ink" />
                Edit
              </button>
            ) : null}
            {space && PERMISSIONS.MOVE_SPARK_IN_SPECTRA(space) ? (
              <button
                onClick={handleMoveToPersonal}
                className="chat-menu-option w-full flex items-center px-3 py-2 text-[12px] text-ink-gray-700 dark:text-ink-gray-200 hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700 rounded-lg transition-all duration-200"
              >
                <LogOut className="chat-menu-option w-3.5 h-3.5 mr-2.5 text-ink-gray-700 dark:text-ink-gray-200" />
                Move to Personal
              </button>
            ) : null}
            {PERMISSIONS.DELETE_SPARK_IN_SPECTRA(space) ? (
              <button
                onClick={() => {
                  handleDotClick(uid); // Close the dropdown
                  deleteSpark(uid, chat);
                }}
                className="chat-menu-option w-full flex items-center px-3 py-2 text-[12px] text-ink-red-600 dark:text-ink-red-400 hover:bg-fill-red-50 dark:hover:bg-fill-red-900/20 rounded-lg transition-all duration-200"
              >
                <DeleteIcon className="chat-menu-option w-3.5 h-3.5 mr-2.5 fill-ink-red-600 dark:fill-ink-red-400" />
                Delete
              </button>
            ) : null}
          </div>,
          document.body
        )}
    </div>
  );
};

const ChatTitleDotMenu = ({
  menuButtonRef,
  chat,
  handleDotClick,
  theme,
  isOpen,
}) => {
  return (
    <button
      ref={menuButtonRef}
      aria-label={`Options for ${chat.title || "Untitled Spark"}`}
      className={`w-5 h-5 flex items-center justify-center rounded-lg transition-colors duration-200 dark:text-ink-white ${
        isOpen ? "bg-fill-gray-200 dark:bg-fill-gray-600" : ""
      } ${chat.pinned ? "" : "menu-button group-hover:opacity-100 opacity-0"}`}
      onClick={() => handleDotClick(chat.uid)}
    >
      {chat.pinned && !isOpen ? (
        <div>
          <PinIconFilled className="w-3 h-3 pin fill-ink-primary dark:fill-ink-accent group-hover:hidden" />
          {theme === "dark" ? (
            <Dot className="w-3 h-3 text-ink-gray-400 hidden group-hover:block" />
          ) : (
            <BlackDot className="w-3 h-3 text-ink-gray-500 hidden group-hover:block" />
          )}
        </div>
      ) : (
        <div className="">
          {theme === "dark" ? (
            <Dot className="w-3 h-3 text-ink-gray-400" />
          ) : (
            <BlackDot className="w-3 h-3 text-ink-gray-500" />
          )}
        </div>
      )}
    </button>
  );
};

export const ChatTitle = ({ theme, chat, space }) => {
  const { colorScheme } = useTheme();
  const isDocs = colorScheme === "docs";
  const { title, uid, pinned, message_count: messageCount } = chat;
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigateLink = space
    ? `/spectra/${space.space_id}/chat/${uid}`
    : `/chat/${uid}`;
  const isActive = location.pathname === navigateLink;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNebulaMode = searchParams.get("mode") === "nebula";
  const user = UserAuthStore((state) => state.user);
  const setShowSidebar = UIStore((state) => state.setShowSidebar);
  
  const isGenerating = useGenerateApiSignal((state) => !!state.signals[uid]?.generate_id);

  const editChatId = ChatStore((state) => state.editChatId);
  const editTitle = ChatStore((state) => state.editTitle);
  const setEditTitle = ChatStore((state) => state.setEditTitle);
  const setEditChatId = ChatStore((state) => state.setEditChatId);
  const setActiveChat = ChatStore((state) => state.setActiveChat);
  const setTempChatId = ChatStore((state) => state.setTempChatId);
  const menuButtonRef = useRef(null);
  const inputRef = useRef(null);
  const setInputFileName = FileUploadStore((state) => state.setInputFileName);
  const setFileSize = FileUploadStore((state) => state.setFileSize);
  const setFileUrl = FileUploadStore((state) => state.setFileUrl);
  const setShowUpload = FileUploadStore((state) => state.setShowUpload);
  const setPrompt = PromptStore((state) => state.setPrompt);
  const [isDragging, setIsDragging] = useState(false);
  const longPressTimerRef = useRef(null);

  const handleTitleChange = (event, uid) => {
    if (event.key === "Enter") {
      if (!event.target.value.trim()) {
        // Reset to original title if input is empty
        setEditTitle(title);
      } else {
        updateChatTitle(uid, event.target.value, space, true, chat);
      }
      setEditChatId(null);
    }
  };

  const handleBlur = (uid) => {
    if (editChatId === uid) {
      const trimmed = editTitle.trim();
      if (trimmed && trimmed !== title) {
        // Save on blur — mirrors Enter behaviour
        updateChatTitle(uid, trimmed, space, true, chat);
      }
      // Empty or unchanged: just close without saving (restores original via parent prop)
      setEditChatId(null);
    }
  };

  const toggleEdit = (uid, title) => {
    setEditChatId(uid);
    setEditTitle(title);

    // Wait for the state to update, then focus on the input
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const RemoveUpload = () => {
    setInputFileName("");
    setFileSize("");
    setFileUrl("");
    setPrompt("");
    setShowUpload(false);
  };

  const handleDotClick = (uid) => {
    if (!menuButtonRef.current) return;

    const rect = menuButtonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    // const spaceAbove = rect.top;
    const requiredSpace = 120;

    if (activeDropdown === uid) {
      setActiveDropdown(null); // Close if already open
    } else {
      setDropdownPosition(
        // spaceBelow < 150 && spaceAbove > 150 ? "top" : "bottom"
        spaceBelow < requiredSpace ? "top" : "bottom"
      );
      setActiveDropdown(uid);
    }
  };

  const handlePinChat = (chatId) => {
    // Call API and React Query will handle update via listeners
    pinChatAPI(chatId, !pinned, space);
  };

  const handleNavClick = () => {
    RemoveUpload();
    setActiveChat(uid);
    setTempChatId(null);
    setActiveDropdown(null);
    FileStore.getState().setFiles([]);
    FileStore.getState().setNewFiles([]);
    FileStore.getState().setFileLoading(false);
    navigate(isNebulaMode ? `${navigateLink}?mode=nebula` : navigateLink);
    
    // Close sidebar on mobile/tablet after navigation
    if (window.innerWidth < 940) {
      setShowSidebar(false);
    }
  };

  const handleOnTouchStart = (e) => {
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null; // Long press detected — stay in sidebar
    }, 400);
  };

  const handleOnTouchEnd = (e) => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      // Short tap — navigate and close sidebar
      navigate(isNebulaMode ? `${navigateLink}?mode=nebula` : navigateLink);
      RemoveUpload();
      setActiveChat(uid);
      setActiveDropdown(null);
      if (window.innerWidth < 940) {
        setShowSidebar(false);
      }
    }
  };

  const handleOnTouchMove = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  useClickOutside([dropdownRef, menuButtonRef], () => setActiveDropdown(null));

  const handleDragStart = (e) => {
    if (chat?.pinned) {
      e.preventDefault(); // Stop drag if pinned
      return;
    }
    setIsDragging(true);
    e.dataTransfer.setData("chat", JSON.stringify(chat));
    if (space?.space_id) e.dataTransfer.setData("space", JSON.stringify(space));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const isDraggable = space?.owner_id
    ? space?.owner_id !== user?.uid
      ? false
      : chat?.pinned
      ? false
      : true
    : chat?.pinned
    ? false
    : true;

  return (
    <li
      className={`chat-draggable w-full nav-item hide-scrollbar relative group select-none ${
        isDragging ? "opacity-70" : ""
      }`}
      draggable={isDraggable && window.innerWidth >= 940}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div
        className={`w-full flex justify-between items-center rounded-md group ${
          isDragging
            ? "border-[0.5px] border-edge-hex-dce2ef dark:border-edge-editor-surface"
            : ""
        }`}
      >
        {editChatId === uid ? (
          <input
            autoFocus
            ref={inputRef}
            type="text"
            className={`rounded-md px-2.5 py-1.5 w-full outline-none text-wrap text-[12px] h-8 bg-fill-white dark:bg-fill-panel-surface border border-edge-gray-200 dark:border-edge-panel-border text-ink-gray-700 dark:text-ink-gray-200 focus:border-edge-primary dark:focus:border-edge-accent transition-colors duration-200`}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={(e) => handleTitleChange(e, uid)}
            onBlur={() => handleBlur(uid)} // Reset title on blur
          />
        ) : (
          <div
            onClick={handleNavClick}
            onTouchStart={handleOnTouchStart}
            onTouchEnd={handleOnTouchEnd}
            onTouchMove={handleOnTouchMove}
            className={`cursor-pointer w-full text-[12px] flex items-center flex-1 px-2.5 h-8 rounded-md no-underline transition-colors duration-200 ${
              isDocs
                ? isActive || isDragging
                  ? "bg-fill-primary/10 text-ink-primary"
                  : "text-ink-gray-500 hover:bg-fill-primary/5"
                : `text-ink-gray-700 dark:text-ink-gray-200 hover:bg-fill-gray-100 dark:hover:bg-fill-hex-303946/100 ${
                    isActive || isDragging
                      ? "bg-fill-sidebar-surface dark:bg-fill-navigation-active border border-edge-primary/20 dark:border-edge-hex-3c5567/100"
                      : ""
                  }`
            }`}
          >
            {isDraggable ? (
              <DragIcon className="fill-ink-gray-500 flex-shrink-0 dark:fill-ink-gray-400 transition-all duration-200 ease-in-out max-w-0 group-hover:max-w-[32px] mr-1.5 w-3 h-3" />
            ) : (
              <DragDisabled className="fill-ink-gray-500 flex-shrink-0 dark:fill-ink-gray-400 transition-all duration-200 ease-in-out max-w-0 group-hover:max-w-[32px] mr-1.5 w-3 h-3" />
            )}
            <span className="flex-grow flex items-center min-w-0 pr-6">
              <span className="truncate text-[12px] leading-tight">
                {title?.length ? `${title.substring(0, 120)}` : ""}
              </span>
              {messageCount > 0 && (
                <span className="flex-shrink-0 ml-1.5 text-[11px] tabular-nums text-ink-hex-64748b dark:text-ink-hex-aaaaaa">
                  ({messageCount})
                </span>
              )}
            </span>
            {isGenerating && (
              <span className="flex-shrink-0 w-2 h-2 rounded-full bg-fill-primary dark:bg-fill-accent animate-pulse" />
            )}
          </div>
        )}

        {editChatId !== uid && (
          <ChatMenu
            menuButtonRef={menuButtonRef}
            activeDropdown={activeDropdown}
            uid={uid}
            handlePinChat={handlePinChat}
            toggleEdit={toggleEdit}
            title={title}
            chat={chat}
            handleDotClick={handleDotClick}
            theme={theme}
            dropdownPosition={dropdownPosition}
            pinned={pinned}
            dropdownRef={dropdownRef}
            space={space}
          />
        )}
      </div>
    </li>
  );
};

export const SidebarChatList = ({ theme, dashboardMode, searchQuery, filteredChats, scrollable = true }) => {
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: moveChatMutate } = useMoveChatAPI();

  // Use React Query for data fetching - read directly without Zustand sync
  const { data: sparksData, isLoading, error } = useGetSparks(orgId, user?.uid, {
    enabled: !!orgId && !!user?.uid,
  });

  // Filter out Spectra sub-sparks (chats with a space field set)
  const sparksOnly = useMemo(() => 
    Array.isArray(sparksData)
      ? sparksData.filter(chat => !chat.space)
      : []
  , [sparksData]);
  
  // Use filtered chats in dashboard mode, otherwise filter sparks by the
  // sidebar search query (case-insensitive on title).
  const chatsToDisplay = useMemo(() => {
    if (dashboardMode) return searchQuery ? filteredChats : sparksOnly;
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return sparksOnly;
    return sparksOnly.filter((chat) => (chat.title || "").toLowerCase().includes(q));
  }, [dashboardMode, searchQuery, filteredChats, sparksOnly]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32 text-sm dark:text-ink-hex-aaaaaa text-ink-copy-hover">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-32 text-sm text-ink-red-500">
        Error loading chats
      </div>
    );
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e) => {
    e.preventDefault();

    const chat = JSON.parse(e.dataTransfer.getData("chat"));
    const fromSpace = e.dataTransfer.getData("space")
      ? JSON.parse(e.dataTransfer.getData("space"))
      : undefined;

    if (fromSpace) {
      // Optimistically add to personal chats immediately
      // Clear the space field so it passes the filter for personal chats
      const chatWithoutSpace = { ...chat, space: null };
      queryClient.setQueryData([QUERYKEYS.CHATS, orgId, user.uid], (old) => {
        if (!old) return [chatWithoutSpace];
        // Avoid duplicates
        if (old.some(c => c.uid === chat.uid)) return old;
        return [chatWithoutSpace, ...old];
      });
      
      // Use mutation with optimistic updates for spectra->personal move
      moveChatMutate({
        old_sid: fromSpace.space_id,
        chat_ids: [chat.uid],
        org_id: orgId,
        uid: user.uid,
        session_id: useSessionIdStore.getState().session_id,
      });
      if (isChatActive(location, chat.uid, fromSpace)) {
        navigate(getNavigateLink(chat.uid));
      }
    }
  };

  return (
    <ChatNavigationLayout
      variant="page"
      className={`side-chat-scroll show-scrollbar-on-hover px-3 ${scrollable ? "h-full overflow-y-auto" : ""}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <ul className="flex flex-col space-y-2">
        {chatsToDisplay.length === 0 ? (
          <div className="text-center text-[12px] italic dark:text-ink-gray-400 text-ink-gray-500 py-2">
            {searchQuery ? "No chats match your search" : "No recent spark"}
          </div>
        ) : (
          chatsToDisplay.map((chat) => (
            <ChatTitle key={chat.uid} chat={chat} theme={theme} />
          ))
        )}
      </ul>
    </ChatNavigationLayout>
  );
};
